import React, { useMemo, useState } from 'react';
import {
  Check, CheckCircle2, Circle, ClipboardList, Loader2, MinusCircle,
  ShieldAlert, Wrench, X, XCircle,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import type { PaJob, PaJobStep, PaJobStepStatus } from '@easyflow/shared';

interface Props {
  job: PaJob;
  onDecide: (id: string, decision: 'approved' | 'rejected', note: string | undefined, expectedVersion: number) => Promise<void>;
  onCancel: (id: string) => Promise<void>;
}

const STEP_ICON: Record<PaJobStepStatus, React.ReactNode> = {
  pending: <Circle className="h-3.5 w-3.5 text-muted-foreground" />,
  running: <Loader2 className="h-3.5 w-3.5 text-ai animate-spin" />,
  completed: <CheckCircle2 className="h-3.5 w-3.5 text-success" />,
  failed: <XCircle className="h-3.5 w-3.5 text-danger" />,
  skipped: <MinusCircle className="h-3.5 w-3.5 text-muted-foreground" />,
};

const StepRow: React.FC<{ step: PaJobStep; index: number }> = ({ step, index }) => (
  <li className="flex items-start gap-2 py-1">
    <span className="mt-0.5 shrink-0">{STEP_ICON[step.status]}</span>
    <span className="min-w-0 flex-1">
      <span className="block text-xs text-foreground">
        <span className="font-medium">{index + 1}. {step.title}</span>
        {step.requiresReview && (
          <span title="Azione irreversibile: richiede conferma esplicita">
            <ShieldAlert className="ml-1 inline h-3 w-3 text-warning" />
          </span>
        )}
      </span>
      {(step.toolName || step.detail) && (
        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
          {step.toolName && (
            <span className="inline-flex items-center gap-0.5 rounded bg-muted px-1 py-0.5 font-mono">
              <Wrench className="h-2.5 w-2.5" />{step.toolName}
            </span>
          )}
          {step.detail && <span className="truncate" title={step.detail}>{step.detail}</span>}
        </span>
      )}
    </span>
  </li>
);

export const JobPlanCard: React.FC<Props> = ({ job, onDecide, onCancel }) => {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { doneCount, total } = useMemo(() => ({
    doneCount: job.plan.filter(s => s.status === 'completed' || s.status === 'skipped').length,
    total: job.plan.length,
  }), [job.plan]);

  const decide = async (decision: 'approved' | 'rejected'): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      await onDecide(job.id, decision, note || undefined, job.statusVersion);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Operazione non riuscita');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      await onCancel(job.id);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Operazione non riuscita');
    } finally {
      setBusy(false);
    }
  };

  const isProposed = job.status === 'proposed';
  const isRunningLike = job.status === 'approved' || job.status === 'running';
  const isAwaitingReview = job.status === 'awaiting_review';
  const isTerminal = job.status === 'completed'
    || job.status === 'rejected'
    || job.status === 'failed'
    || job.status === 'cancelled';

  const headerLabel = isProposed
    ? 'Piano proposto — richiede la tua approvazione'
    : job.status === 'approved'
      ? 'Piano approvato — in avvio…'
      : job.status === 'running'
        ? `Lavoro in corso — ${doneCount}/${total} passi`
        : isAwaitingReview
          ? 'Bozze pronte — conferma per procedere'
          : job.status === 'completed'
            ? 'Completato'
            : job.status === 'rejected'
              ? 'Piano rifiutato'
              : job.status === 'failed'
                ? 'Non completato'
                : 'Annullato';

  const tone = isProposed || isAwaitingReview
    ? 'border-warning/40 bg-warning/10'
    : job.status === 'completed'
      ? 'border-success/30 bg-success/5'
      : job.status === 'failed'
        ? 'border-danger/30 bg-danger/5'
        : isTerminal
          ? 'border-border bg-muted/30'
          : 'border-ai/30 bg-ai/5';

  return (
    <div className={`w-full max-w-[560px] rounded-lg border p-3 ${tone}`}
    >
      <div className="flex items-start gap-2">
        <ClipboardList className={`mt-0.5 h-4 w-4 shrink-0 ${isProposed || isAwaitingReview ? 'text-warning' : 'text-ai'}`} />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-foreground">{job.title}</div>
          <div className="text-[10px] text-muted-foreground">{headerLabel}</div>
        </div>
      </div>

      {(isRunningLike || isAwaitingReview) && total > 0 && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-ai transition-all duration-500"
            style={{ width: `${Math.round((doneCount / total) * 100)}%` }}
          />
        </div>
      )}

      <ul className="mt-2 divide-y divide-border/50">
        {job.plan.map((s, i) => <StepRow key={s.id} step={s} index={i} />)}
      </ul>

      {isAwaitingReview && (
        <div className="mt-1 text-[10px] text-warning">
          Le azioni irreversibili sono in bozza: confermale (o rifiutale) dalle card Conferme qui sotto.
        </div>
      )}

      {job.status === 'completed' && job.resultSummary && (
        <div className="mt-2 rounded bg-success/10 px-2 py-1.5 text-xs text-success">
          {job.resultSummary}
        </div>
      )}

      {job.status === 'failed' && job.error && (
        <div className="mt-2 rounded bg-danger/10 px-2 py-1.5 text-xs text-danger">
          {job.error}
        </div>
      )}

      {(job.status === 'rejected' || job.status === 'cancelled') && job.decisionNote && (
        <div className="mt-2 text-xs text-muted-foreground">{job.decisionNote}</div>
      )}

      {isProposed && (
        <>
          {showNote ? (
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Nota per il PA (es. modifica al piano, motivo del rifiuto)…"
              rows={2}
              className="mt-2 w-full resize-none rounded border border-border bg-card px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ai"
            />
          ) : (
            <button
              type="button"
              className="mt-1.5 text-[10px] text-muted-foreground underline hover:text-foreground"
              onClick={() => setShowNote(true)}
            >
              Aggiungi una nota (modifica a parole)
            </button>
          )}
          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              disabled={busy}
              className="h-7 bg-success px-3 text-xs text-success-foreground hover:bg-success/90"
              onClick={() => { void decide('approved'); }}
            >
              {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}
              Approva piano
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              className="h-7 px-3 text-xs text-danger hover:bg-danger/10"
              onClick={() => { void decide('rejected'); }}
            >
              <X className="mr-1 h-3 w-3" /> Rifiuta
            </Button>
          </div>
        </>
      )}

      {(isRunningLike || isAwaitingReview) && (
        <div className="mt-2 flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            className="h-7 px-3 text-xs text-danger hover:bg-danger/10"
            onClick={() => { void cancel(); }}
          >
            {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <X className="mr-1 h-3 w-3" />}
            {isRunningLike ? 'Interrompi lavoro' : 'Annulla lavoro'}
          </Button>
        </div>
      )}
      {actionError && <div className="mt-2 text-xs text-danger">{actionError}</div>}
    </div>
  );
};
